import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

function Logo() {
  return (
    <div className="auth-logo">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="52" height="52" aria-hidden="true">
        <defs>
          <linearGradient id="agLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0EA5E9"/>
            <stop offset="100%" stopColor="#0369A1"/>
          </linearGradient>
          <linearGradient id="agBoltGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF"/>
            <stop offset="100%" stopColor="#BAE6FD"/>
          </linearGradient>
          <mask id="agBiteMask">
            <rect x="0" y="0" width="48" height="48" rx="13" fill="white"/>
            <circle cx="44" cy="4" r="11" fill="black"/>
          </mask>
        </defs>
        <rect x="0" y="0" width="48" height="48" rx="13" fill="url(#agLogoGrad)" mask="url(#agBiteMask)"/>
        <rect x="0" y="0" width="48" height="48" rx="13" fill="rgba(255,255,255,0.07)" mask="url(#agBiteMask)"/>
        <path d="M27 9 L15.5 27 H22.5 L20.5 39 L32.5 21 H25.5 Z" fill="url(#agBoltGrad)"/>
      </svg>
      <span className="auth-logo-text">BiteLearning</span>
    </div>
  );
}

// ── Sign Up Form ─────────────────────────────────────────────────────────────

function SignUpForm({ onSuccess }) {
  const { signUp } = useAuth();
  const [form, setForm]     = useState({ displayName: '', email: '', phone: '', password: '', confirm: '' });
  const [error, setError]   = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy]     = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) return setError('Passwords do not match.');
    if (form.password.length < 6)       return setError('Password must be at least 6 characters.');
    setBusy(true);
    const { data, error: err } = await signUp({
      email:       form.email.trim(),
      password:    form.password,
      displayName: form.displayName.trim(),
      phone:       form.phone.trim(),
    });
    setBusy(false);
    if (err) return setError(err.message);
    // Supabase sends a confirmation email by default
    if (data?.user && !data.session) {
      setNotice('Account created! Check your email to confirm, then sign in.');
    } else {
      onSuccess?.();
    }
  };

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      {notice && <div className="auth-notice">{notice}</div>}
      {error  && <div className="auth-error">{error}</div>}

      <div className="auth-field">
        <label>Full Name</label>
        <input type="text" placeholder="Your name" value={form.displayName}
          onChange={set('displayName')} required disabled={busy} />
      </div>

      <div className="auth-field">
        <label>Email</label>
        <input type="email" placeholder="you@example.com" value={form.email}
          onChange={set('email')} required disabled={busy} />
      </div>

      <div className="auth-field">
        <label>Phone <span className="auth-optional">(optional)</span></label>
        <input type="tel" placeholder="+1 555 000 0000" value={form.phone}
          onChange={set('phone')} disabled={busy} />
      </div>

      <div className="auth-field">
        <label>Password</label>
        <input type="password" placeholder="Min. 6 characters" value={form.password}
          onChange={set('password')} required disabled={busy} />
      </div>

      <div className="auth-field">
        <label>Confirm Password</label>
        <input type="password" placeholder="Repeat password" value={form.confirm}
          onChange={set('confirm')} required disabled={busy} />
      </div>

      <button type="submit" className="auth-btn auth-btn--primary" disabled={busy}>
        {busy ? 'Creating account…' : 'Create Account'}
      </button>
    </form>
  );
}

// ── Forgot Password Form ─────────────────────────────────────────────────────

function ForgotPasswordForm({ onBack }) {
  const [email,  setEmail]  = useState('');
  const [sent,   setSent]   = useState(false);
  const [error,  setError]  = useState('');
  const [busy,   setBusy]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    });
    setBusy(false);
    if (err) return setError(err.message);
    setSent(true);
  };

  if (sent) {
    return (
      <div className="auth-form">
        <div className="auth-notice">
          ✅ Check your email — we sent a password reset link to <strong>{email}</strong>.
        </div>
        <button type="button" className="auth-link-btn auth-back-btn" onClick={onBack}>
          ← Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      {error && <div className="auth-error">{error}</div>}
      <p className="auth-form-desc">Enter your email and we'll send you a reset link.</p>

      <div className="auth-field">
        <label>Email</label>
        <input type="email" placeholder="you@example.com" value={email}
          onChange={e => setEmail(e.target.value)} required disabled={busy} />
      </div>

      <button type="submit" className="auth-btn auth-btn--primary" disabled={busy}>
        {busy ? 'Sending…' : 'Send Reset Link'}
      </button>
      <button type="button" className="auth-link-btn auth-back-btn" onClick={onBack}>
        ← Back to Sign In
      </button>
    </form>
  );
}

// ── Sign In Form ─────────────────────────────────────────────────────────────

function SignInForm({ onSuccess, onForgot }) {
  const { signIn } = useAuth();
  const [form, setForm]   = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const { error: err } = await signIn({ email: form.email.trim(), password: form.password });
    setBusy(false);
    if (err) return setError(err.message);
    onSuccess?.();
  };

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      {error && <div className="auth-error">{error}</div>}

      <div className="auth-field">
        <label>Email</label>
        <input type="email" placeholder="you@example.com" value={form.email}
          onChange={set('email')} required disabled={busy} />
      </div>

      <div className="auth-field">
        <label>
          Password
          <button type="button" className="auth-link-btn auth-forgot-link" onClick={onForgot}>
            Forgot password?
          </button>
        </label>
        <input type="password" placeholder="Your password" value={form.password}
          onChange={set('password')} required disabled={busy} />
      </div>

      <button type="submit" className="auth-btn auth-btn--primary" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign In'}
      </button>
    </form>
  );
}

// ── Social OAuth Buttons ─────────────────────────────────────────────────────

function SocialButtons() {
  const { signInWithGoogle } = useAuth();
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState('');

  const handleGoogle = async () => {
    setError('');
    setBusy(true);
    const { error: err } = await signInWithGoogle();
    if (err) { setError(err.message); setBusy(false); }
    // on success Supabase redirects the page — no need to reset busy
  };

  return (
    <div className="social-btns">
      {error && <div className="auth-error">{error}</div>}
      <button className="social-btn social-btn--google" onClick={handleGoogle} disabled={busy}>
        {busy ? 'Redirecting…' : (
          <>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </>
        )}
      </button>
    </div>
  );
}

// ── Vision Panel ─────────────────────────────────────────────────────────────

const VISION_PILLARS = [
  {
    icon: '🚀',
    title: 'The future of learning is here',
    desc: 'AI now makes it possible to get a complete, expert-quality lesson on any topic in seconds — no textbooks, no waiting, no fluff.',
  },
  {
    icon: '😄',
    title: 'Learning is fun again',
    desc: 'Bite-sized lessons, read-aloud audio, and instant quizzes turn every curious moment into something genuinely enjoyable.',
  },
  {
    icon: '🤝',
    title: 'Better together',
    desc: 'Share what you learn with friends, family, and colleagues — inside a BiteLearning community or on WhatsApp, Facebook, LinkedIn, or wherever your people are.',
  },
  {
    icon: '🌍',
    title: 'Build your learning tribe',
    desc: 'Create or join communities around any subject. When curiosity becomes a shared journey, knowledge sticks — and grows.',
  },
];

function VisionPanel() {
  return (
    <div className="auth-vision">
      <div className="auth-vision-inner">
        {/* Brand */}
        <div className="auth-vision-brand">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="44" height="44" aria-hidden="true">
            <defs>
              <linearGradient id="vpLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#0EA5E9"/>
                <stop offset="100%" stopColor="#0369A1"/>
              </linearGradient>
              <linearGradient id="vpBoltGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFFFFF"/>
                <stop offset="100%" stopColor="#BAE6FD"/>
              </linearGradient>
              <mask id="vpBiteMask">
                <rect x="0" y="0" width="48" height="48" rx="13" fill="white"/>
                <circle cx="44" cy="4" r="11" fill="black"/>
              </mask>
            </defs>
            <rect x="0" y="0" width="48" height="48" rx="13" fill="url(#vpLogoGrad)" mask="url(#vpBiteMask)"/>
            <rect x="0" y="0" width="48" height="48" rx="13" fill="rgba(255,255,255,0.07)" mask="url(#vpBiteMask)"/>
            <path d="M27 9 L15.5 27 H22.5 L20.5 39 L32.5 21 H25.5 Z" fill="url(#vpBoltGrad)"/>
          </svg>
          <span className="auth-vision-name">BiteLearning</span>
        </div>

        {/* Vision headline */}
        <h1 className="auth-vision-headline">
          Learn. Share.<br />Grow together.
        </h1>

        {/* Tagline */}
        <p className="auth-vision-mission">
          We believe the best learning happens when curiosity meets community.
          BiteLearning gives you both — instant AI lessons on anything, and the
          people to share them with.
        </p>

        {/* Vision pillars */}
        <ul className="auth-vision-features">
          {VISION_PILLARS.map(p => (
            <li key={p.title} className="auth-vision-feature">
              <span className="auth-vision-icon">{p.icon}</span>
              <div>
                <strong>{p.title}</strong>
                <span>{p.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Main AuthGate ─────────────────────────────────────────────────────────────

function AuthGate({ compact = false }) {
  const { continueAsGuest } = useAuth();
  const [tab, setTab] = useState('signup'); // 'signup' | 'signin' | 'forgot'

  return (
    <div className={`auth-gate${compact ? ' auth-gate--compact' : ''}`}>

      {/* Left — vision / branding (hidden in compact/exam mode) */}
      {!compact && <VisionPanel />}

      {/* Right — auth card */}
      <div className="auth-card">
        {!compact && <Logo />}
        {!compact && <p className="auth-tagline">Bite-sized learning · Learn smart, learn fast</p>}

        {/* Social login — hidden during forgot flow */}
        {tab !== 'forgot' && <SocialButtons />}

        {tab !== 'forgot' && <div className="auth-divider"><span>or use email</span></div>}

        {/* Tab switcher — hidden during forgot flow */}
        {tab !== 'forgot' && (
          <div className="auth-tabs">
            <button
              className={`auth-tab ${tab === 'signin' ? 'auth-tab--active' : ''}`}
              onClick={() => setTab('signin')}
            >Sign In</button>
            <button
              className={`auth-tab ${tab === 'signup' ? 'auth-tab--active' : ''}`}
              onClick={() => setTab('signup')}
            >Create Account</button>
          </div>
        )}

        {tab === 'forgot'
          ? <ForgotPasswordForm onBack={() => setTab('signin')} />
          : tab === 'signin'
            ? <SignInForm onForgot={() => setTab('forgot')} />
            : <SignUpForm />
        }

        {tab !== 'forgot' && (
          <>
            <div className="auth-divider"><span>or</span></div>
            <button className="auth-btn auth-btn--ghost" onClick={continueAsGuest}>
              Continue as Guest
            </button>
            <p className="auth-guest-note">
              Guest progress is saved on this device only.
              Create an account to access your history anywhere.
            </p>
          </>
        )}

        {/* Mobile-only copyright */}
        <p className="auth-copy-mobile">
          © {new Date().getFullYear()} BiteLearning. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default AuthGate;
